import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import { themeable } from '@instructure/ui-themeable';
import classnames from 'classnames';
import { partition } from 'lodash';
import { arrayOf, string, number, shape, func } from 'prop-types';
import moment from 'moment-timezone';
import { userShape, itemShape, sizeShape } from '../plannerPropTypes';
const styles = {
  componentId: 'eOFoE',
  template: function template(theme) {
    return "\n\n.root_3bek{border-color:".concat(theme.groupColor || 'inherit', ";color:").concat(theme.groupColor || 'inherit', ";display:flex;font-family:").concat(theme.fontFamily || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";margin:").concat(theme.margin || 'inherit', "}\n\n.root_3bek,.title_3bek{position:relative}\n\n.title_3bek{-ms-hyphens:auto;-webkit-hyphens:auto;-webkit-text-decoration:").concat(theme.titleTextDecoration || 'inherit', ";background-color:").concat(theme.titleBackground || 'inherit', ";box-sizing:border-box;color:").concat(theme.titleColor || 'inherit', ";flex:1;font-size:").concat(theme.titleFontSize || 'inherit', ";font-weight:").concat(theme.titleFontWeight || 'inherit', ";hyphens:auto;max-height:3rem;min-width:1px;overflow:hidden;overflow-wrap:break-word;padding:").concat(theme.titlePadding || 'inherit', ";text-align:center;text-decoration:").concat(theme.titleTextDecoration || 'inherit', ";text-transform:").concat(theme.titleTextTransform || 'inherit', ";word-wrap:break-word;z-index:1}\n\n[dir=ltr] .title_3bek,[dir=rtl] .title_3bek{text-align:center}\n\n.title_3bek:after{background:linear-gradient(180deg,hsla(0,0%,100%,0) 0%,").concat(theme.titleBackground || 'inherit', ");bottom:0;content:\"\";height:").concat(theme.titleOverflowGradientHeight || 'inherit', ";left:0;position:absolute;width:100%}\n\n.hero_3bek{align-items:center;background-position:50%;background-repeat:no-repeat;background-size:cover;box-sizing:border-box;display:flex;flex:0 0 ").concat(theme.heroWidth || 'inherit', ";justify-content:center;min-width:1px;outline:none;padding:").concat(theme.heroPadding || 'inherit', ";position:relative;text-decoration:none}\n\n.hero_3bek .groupingName_3bek{-webkit-text-decoration:").concat(theme.heroLinkTextDecoration || 'inherit', ";text-decoration:").concat(theme.heroLinkTextDecoration || 'inherit', "}\n\n.hero_3bek,.overlay_3bek{border-bottom-inline-start-radius:").concat(theme.heroBorderRadius || 'inherit', ";border-top-inline-start-radius:").concat(theme.heroBorderRadius || 'inherit', "}\n\n[dir=ltr] .hero_3bek,[dir=ltr] .overlay_3bek{border-bottom-left-radius:").concat(theme.heroBorderRadius || 'inherit', ";border-top-left-radius:").concat(theme.heroBorderRadius || 'inherit', "}\n\n[dir=rtl] .hero_3bek,[dir=rtl] .overlay_3bek{border-bottom-right-radius:").concat(theme.heroBorderRadius || 'inherit', ";border-top-right-radius:").concat(theme.heroBorderRadius || 'inherit', "}\n\n.heroHover_3bek:focus,.heroHover_3bek:hover{text-decoration:none}\n\n.heroHover_3bek:focus .title_3bek,.heroHover_3bek:hover .title_3bek{-webkit-text-decoration:").concat(theme.titleTextDecorationHover || 'inherit', ";text-decoration:").concat(theme.titleTextDecorationHover || 'inherit', "}\n\n.overlay_3bek{background-color:").concat(theme.groupColor || 'inherit', ";height:100%;left:0;opacity:1;position:absolute;top:0;width:100%}\n\n.overlay_3bek.withImage_3bek{opacity:").concat(theme.overlayOpacity || 'inherit', "}\n\n.items_3bek{border-color:").concat(theme.groupColor || 'inherit', ";border-top:").concat(theme.borderTopWidth || 'inherit', " solid;color:").concat(theme.groupColor || 'inherit', ";flex:1;list-style-type:none;margin:0;min-width:1px;padding:0}\n\n.medium_3bek.root_3bek,.small_3bek.root_3bek{display:block;margin:0}\n\n.medium_3bek .hero_3bek,.medium_3bek .overlay_3bek,.small_3bek .hero_3bek,.small_3bek .overlay_3bek{background-color:initial;border-radius:0}\n\n.medium_3bek .hero_3bek,.small_3bek .hero_3bek{display:block;flex:none;line-height:2rem;min-height:unset}\n\n.medium_3bek .title_3bek,.small_3bek .title_3bek{-webkit-padding-start:0;font-size:").concat(theme.titleFontSizeTablet || 'inherit', ";padding-inline-start:0}\n\n[dir=ltr] .medium_3bek .title_3bek,[dir=ltr] .small_3bek .title_3bek{padding-left:0}\n\n[dir=rtl] .medium_3bek .title_3bek,[dir=rtl] .small_3bek .title_3bek{padding-right:0}\n\n.medium_3bek .items_3bek,.small_3bek .items_3bek{border-top-width:").concat(theme.borderTopWidthTablet || 'inherit', "}");
  },
  'root': 'root_3bek',
  'title': 'title_3bek',
  'hero': 'hero_3bek',
  'groupingName': 'groupingName_3bek',
  'overlay': 'overlay_3bek',
  'heroHover': 'heroHover_3bek',
  'withImage': 'withImage_3bek',
  'items': 'items_3bek',
  'medium': 'medium_3bek',
  'small': 'small_3bek'
};
import theme from './theme.js';
import PlannerItem from '../PlannerItem';
import CompletedItemsFacade from '../CompletedItemsFacade';
import NotificationBadge, { MissingIndicator, NewActivityIndicator } from '../NotificationBadge';
import formatMessage from '../../format-message';
import { getBadgesForItem, getBadgesForItems, showPillForOverdueStatus } from '../../utilities/statusUtils';
import { animatable } from '../../dynamic-ui';
import responsiviser from '../responsiviser';
export class Grouping extends Component {
  constructor(props) {
    super(props);

    this.groupingLinkRef = link => {
      this.groupingLink = link;
    };

    this.getFocusable = () => {
      return this.groupingLink;
    };

    this.handleFacadeClick = e => {
      if (e) {
        e.preventDefault();
      }

      this.setState(() => ({
        showCompletedItems: true
      }), () => {
        if (this.groupingLink) this.groupingLink.focus();
      });
    };

    this.state = {
      showCompletedItems: false,
      badgeMap: this.setupItemBadgeMap(props.items)
    };
  }

  componentDidMount() {
    this.props.registerAnimatable('group', this, this.props.animatableIndex, this.itemUniqueIds());
  }

  componentWillReceiveProps(newProps) {
    this.props.deregisterAnimatable('group', this, this.itemUniqueIds());
    this.props.registerAnimatable('group', this, newProps.animatableIndex, this.itemUniqueIds(newProps));
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('group', this, this.itemUniqueIds());
  }

  itemUniqueIds() {
    let props = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : this.props;
    return props.items.map(item => item.uniqueId);
  }

  setupItemBadgeMap(items) {
    const mapping = {};
    items.forEach(item => {
      const badges = getBadgesForItem(item);
      if (badges.length) mapping[item.id] = badges;
    });
    return mapping;
  }

  getScrollable() {
    return this.groupingLink || this.plannerNoteHero;
  }

  getLayout() {
    return this.props.responsiveSize;
  }

  renderItemsAndFacade(items) {
    const _partition = partition(items, item => item.completed && !item.show),
          _partition2 = _slicedToArray(_partition, 2),
          completedItems = _partition2[0],
          otherItems = _partition2[1];

    let itemsToRender = otherItems;

    if (this.state.showCompletedItems) {
      itemsToRender = items;
    }

    const componentsToRender = this.renderItems(itemsToRender);
    componentsToRender.push(this.renderFacade(completedItems, this.props.animatableIndex * 100 + itemsToRender.length + 1));
    return componentsToRender;
  }

  renderItems(items) {
    const showNotificationBadgeOnItem = this.getLayout() !== 'large';
    return items.map((item, itemIndex) => React.createElement("li", {
      className: styles.item,
      key: item.uniqueId
    }, React.createElement(PlannerItem, {
      theme: {
        iconColor: this.props.color
      },
      color: this.props.color,
      completed: item.completed,
      overrideId: item.overrideId,
      id: item.id,
      uniqueId: item.uniqueId,
      animatableIndex: this.props.animatableIndex * 100 + itemIndex + 1,
      courseName: this.props.title,
      context: item.context || {},
      date: moment(item.date).tz(this.props.timeZone),
      associated_item: item.type,
      title: item.title,
      points: item.points,
      updateTodo: this.props.updateTodo,
      html_url: item.html_url,
      toggleCompletion: () => this.props.toggleCompletion(item),
      badges: this.state.badgeMap[item.id],
      details: item.details,
      toggleAPIPending: item.toggleAPIPending,
      status: item.status,
      newActivity: item.newActivity,
      allDay: item.allDay,
      showNotificationBadge: showNotificationBadgeOnItem,
      currentUser: this.props.currentUser,
      feedback: item.feedback,
      location: item.location,
      address: item.address,
      endTime: item.endTime,
      dateStyle: item.dateStyle,
      timeZone: this.props.timeZone
    })));
  }

  renderFacade(completedItems, animatableIndex) {
    const showNotificationBadgeOnItem = this.getLayout() !== 'large';

    if (!this.state.showCompletedItems && completedItems.length > 0) {
      const theDay = completedItems[0].date.clone();
      theDay.startOf('day');
      let missing = false;
      let newActivity = false;
      const completedItemIds = completedItems.map(item => {
        if (showPillForOverdueStatus('missing', item)) missing = true;
        if (item.newActivity) newActivity = true;
        return item.uniqueId;
      });
      let notificationBadge = 'none';

      if (showNotificationBadgeOnItem) {
        if (newActivity) {
          notificationBadge = 'newActivity';
        } else if (missing) {
          notificationBadge = 'missing';
        }
      }

      return React.createElement("li", {
        className: styles.item,
        key: "completed"
      }, React.createElement(CompletedItemsFacade, {
        onClick: this.handleFacadeClick,
        itemCount: completedItems.length,
        badges: getBadgesForItems(completedItems),
        animatableIndex: animatableIndex,
        animatableItemIds: completedItemIds,
        notificationBadge: notificationBadge,
        theme: {
          labelColor: this.props.color
        },
        date: theDay,
        responsiveSize: this.props.responsiveSize
      }));
    }

    return null;
  }

  renderToDoText() {
    return formatMessage('To Do');
  }

  renderNotificationBadge() {
    // narrower layout puts the indicator next to the actual items
    if (this.getLayout() !== 'large') {
      return null;
    }

    let missing = false;
    const newItem = this.props.items.find(item => {
      if (showPillForOverdueStatus('missing', item)) missing = true;
      return item.newActivity;
    });

    if (newItem || missing) {
      const IndicatorComponent = newItem ? NewActivityIndicator : MissingIndicator;
      const badgeMessage = this.props.title ? this.props.title : this.renderToDoText();
      return React.createElement(NotificationBadge, null, React.createElement(IndicatorComponent, {
        title: badgeMessage,
        itemIds: this.itemUniqueIds(),
        animatableIndex: this.props.animatableIndex,
        getFocusable: this.getFocusable
      }));
    } else {
      return React.createElement(NotificationBadge, null);
    }
  } // I wouldn't have broken the background and title apart, but wrapping them in a container span breaks styling


  renderGroupLinkBackground() {
    const clazz = classnames({
      [styles.overlay]: true,
      [styles.withImage]: this.props.image_url
    });
    const style = this.getLayout() === 'large' ? {
      backgroundColor: this.props.color
    } : null;
    return React.createElement("span", {
      className: clazz,
      style: style
    });
  }

  renderGroupLinkTitle() {
    return React.createElement("span", {
      className: styles.title
    }, this.props.title || this.renderToDoText());
  }

  renderGroupLink() {
    if (!this.props.title) {
      return React.createElement("span", {
        className: styles.hero,
        ref: elt => this.plannerNoteHero = elt
      }, this.renderGroupLinkBackground(), this.renderGroupLinkTitle());
    }

    const style = this.getLayout() === 'large' ? {
      backgroundImage: "url(".concat(this.props.image_url || '', ")")
    } : null;
    return React.createElement("a", {
      href: this.props.url || '#',
      ref: this.groupingLinkRef,
      className: "".concat(styles.hero, " ").concat(styles.heroHover),
      style: style
    }, this.renderGroupLinkBackground(), this.renderGroupLinkTitle());
  }

  render() {
    return React.createElement("div", {
      className: classnames(styles.root, styles[this.getLayout()], 'planner-grouping')
    }, this.renderNotificationBadge(), this.renderGroupLink(), React.createElement("ol", {
      className: styles.items,
      style: {
        borderColor: this.props.color
      }
    }, this.renderItemsAndFacade(this.props.items)));
  }

}
Grouping.propTypes = {
  items: arrayOf(shape(itemShape)).isRequired,
  animatableIndex: number,
  title: string,
  color: string,
  image_url: string,
  timeZone: string.isRequired,
  url: string,
  toggleCompletion: func,
  updateTodo: func,
  registerAnimatable: func,
  deregisterAnimatable: func,
  currentUser: shape(userShape),
  responsiveSize: sizeShape
};
Grouping.defaultProps = {
  registerAnimatable: () => {},
  deregisterAnimatable: () => {},
  responsiveSize: 'large'
};
const ResponsiveGrouping = responsiviser()(Grouping);
export default animatable(themeable(theme, styles)(ResponsiveGrouping));